import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { datasheetsApi } from "@/lib/datasheets";
import { cn } from "@/lib/utils";

export interface DatasheetPickerValue {
  datasheetId: number;
  label: string;
}

export interface DatasheetPickerProps {
  value: DatasheetPickerValue | null;
  onChange: (v: DatasheetPickerValue | null) => void;
  placeholder?: string;
}

const labelFor = (r: { companyName: string | null; datasheetTypeDescription: string | null }) =>
  [r.companyName ?? "—", r.datasheetTypeDescription ?? ""].filter(Boolean).join(" — ");

/**
 * There is no datasheet-search endpoint beyond `q`, so this loads one generous page of
 * datasheets and filters client-side (cmdk's default CommandInput filtering), mirroring
 * CompanyPicker.
 */
export function DatasheetPicker({ value, onChange, placeholder }: DatasheetPickerProps) {
  const [open, setOpen] = useState(false);
  const { data } = useQuery({
    queryKey: ["datasheets", "picker"],
    queryFn: () => datasheetsApi.list({ pageSize: 500 }),
  });
  const rows = useMemo(() => data?.items ?? [], [data]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-label="Datasheet"
          aria-expanded={open}
          className="w-full justify-between"
        >
          <span className="truncate">{value ? value.label : (placeholder ?? "Select datasheet...")}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[300px]" align="start">
        <Command>
          <CommandInput placeholder="Search datasheets..." />
          <CommandList>
            <CommandEmpty>No datasheets found.</CommandEmpty>
            <CommandGroup>
              {rows.map((r) => {
                const label = labelFor(r);
                return (
                  <CommandItem
                    key={r.datasheetId}
                    value={label}
                    onSelect={() => {
                      onChange({ datasheetId: r.datasheetId, label });
                      setOpen(false);
                    }}
                  >
                    <Check className={cn("mr-2 h-4 w-4", value?.datasheetId === r.datasheetId ? "opacity-100" : "opacity-0")} />
                    {label}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
